"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { FaSearch, FaPlus, FaEdit, FaBuilding, FaFileExcel, FaFilter, FaUser, FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import { Pagination } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { useAuth } from "../../provider/auth.provider"
import axiosInstance from "../../libs/axios"
import { useDebouncedCallback } from "../../hooks/useDebounce"

interface Client {
  id: number
  client_full_name: string
  client_short_name: string
  is_active: boolean
}

interface Project {
  id: number
  project_name: string
  status: string
  created_at: string
}

interface ClientProject {
  id: number
  client_id: number
  client_project_name: string
  client_project_description?: string
  is_active: boolean
  created_at: string
  updated_at: string
  created_by: number
  suspended_by?: number
  suspended_at?: string
  client?: Client
  projects?: Project[]
  project_count?: number
}

interface ClientProjectsListProps {
  clientProjects: ClientProject[]
  clients: Client[]
  pagination: {
    offset: number
    limit: number
    page: number
    total_pages: number
    total_count: number
    has_next_page: boolean
    has_prev_page: boolean
    next_page: number | null
    prev_page: number | null
  }
  onSearch: (search: string) => void
  onStatusFilter: (status: string) => void
  onClientFilter: (clientId: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  onAddClientProject: () => void
  onEditClientProject: (clientProject: ClientProject) => void
  onDeleteClientProject: (clientProject: ClientProject) => void
  onToggleStatus: (clientProject: ClientProject) => void
  loading?: boolean
  onSort?: (field: string, direction: 'ASC' | 'DESC') => void
  searchValue?: string
  statusValue?: string
  clientValue?: string
  sortState?: { field: string; direction: 'ASC' | 'DESC' }
}

export const ClientProjectsList: React.FC<ClientProjectsListProps> = ({
  clientProjects,
  clients,
  pagination,
  onSearch,
  onStatusFilter,
  onClientFilter,
  onPageChange,
  onLimitChange,
  onAddClientProject,
  onEditClientProject,
  onDeleteClientProject,
  onToggleStatus,
  loading = false,
  onSort,
  searchValue = "",
  statusValue = "",
  clientValue = "",
  sortState
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [clientFilter, setClientFilter] = useState("")
  const [idFilter, setIdFilter] = useState("")
  const [clientIdFilter, setClientIdFilter] = useState("")
  const [projectNameFilter, setProjectNameFilter] = useState("")
  const [descriptionFilter, setDescriptionFilter] = useState("")
  const [projectCountFilter, setProjectCountFilter] = useState("")
  const [createdByFilter, setCreatedByFilter] = useState("")
  const [suspendedByFilter, setSuspendedByFilter] = useState("")
  const [createdAtFilter, setCreatedAtFilter] = useState("")
  const [updatedAtFilter, setUpdatedAtFilter] = useState("")
  const [suspendedAtFilter, setSuspendedAtFilter] = useState("")
  const [showExcelModal, setShowExcelModal] = useState(false)
  const { hasPermission } = useAuth()
  const [sortConfig, setSortConfig] = useState<{
    field: string
    direction: 'ASC' | 'DESC'
  }>({
    field: 'created_at',
    direction: 'DESC'
  })
  const [exporting, setExporting] = useState(false)
  const [exportData, setExportData] = useState<Record<string, any>[]>([])

  useEffect(() => {
    setSearchTerm(searchValue || "")
  }, [searchValue])

  useEffect(() => {
    setStatusFilter(statusValue || "")
  }, [statusValue])

  useEffect(() => {
    setClientFilter(clientValue || "")
  }, [clientValue])

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState)
    }
  }, [sortState])

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedOnSearch(value)
  }

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setStatusFilter(value)
    onStatusFilter(value)
  }

  const handleClientFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setClientFilter(value)
    onClientFilter(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Suspended
      </span>
    )
  }

  const getClientProjectIcon = () => {
    return <FaBuilding className="w-4 h-4 text-blue-600" />
  }

  const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : "")

  const mapClientProjectForExport = (clientProject: ClientProject) => {
    const projectsInfo = clientProject.projects?.map((project) => project.project_name).filter(Boolean) || []
    const projectDetails = clientProject.projects?.map((project) => `${project.project_name || 'N/A'} (${project.status || 'N/A'})`).filter(Boolean) || []

    return {
      "Client Name": clientProject.client?.client_full_name || "",
      "Client Short Name": clientProject.client?.client_short_name || "",
      "Project Name": clientProject.client_project_name,
      Description: clientProject.client_project_description || "",
      Status: clientProject.is_active ? "Active" : "Suspended",
      "Projects Count": clientProject.project_count ?? clientProject.projects?.length ?? 0,
      "Project Names": projectsInfo.join(", "),
      "Project Details": projectDetails.join("; "),
      // "Created By": clientProject.created_by,
      // "Suspended By": clientProject.suspended_by || "N/A",
      "Created At": formatDateTime(clientProject.created_at),
      "Updated At": formatDateTime(clientProject.updated_at),
      "Suspended At": formatDateTime(clientProject.suspended_at),
    }
  }

  const buildExportParams = () => {
    const params: Record<string, any> = {}
    if (searchValue?.trim()) params.search = searchValue.trim()
    if (statusValue?.trim()) params.status = statusValue.trim()
    if (clientValue?.trim()) params.client_id = clientValue.trim()

    const activeSort = sortState || sortConfig
    if (activeSort?.field) {
      params.sort_by = activeSort.field
      params.sort_order = activeSort.direction
    }

    return params
  }

  const fetchExportData = async () => {
    try {
      setExporting(true)
      const params = buildExportParams()
      const limit = 100
      let page = 1
      let totalPages = 1
      const allClientProjects: ClientProject[] = []

      do {
        const response = await axiosInstance.get('/client-projects/list', {
          params: {
            ...params,
            page,
            limit,
          },
        })

        const responseData = response.data?.data
        const pageData: ClientProject[] = responseData?.client_projects || []
        allClientProjects.push(...pageData)

        const paginationInfo = responseData?.pagination
        if (paginationInfo) {
          totalPages = paginationInfo.total_pages ?? paginationInfo.totalPages ?? (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : totalPages)
        } else if (pageData.length < limit) {
          totalPages = page
        }

        page += 1
      } while (page <= totalPages)

      setExportData(allClientProjects.map(mapClientProjectForExport))
      setShowExcelModal(true)
    } catch (error) {
      console.error('Error exporting client projects:', error)
    } finally {
      setExporting(false)
    }
  }

  const handleSort = (field: string) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
    setSortConfig({ field, direction })
    onSort?.(field, direction)
  }

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />
    return sortConfig.direction === "ASC" ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />
  }

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Client Projects Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
            {hasPermission("ClientProject", "create") && (
              <button
                onClick={onAddClientProject}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add Client Project
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Client Projects</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
              <input
                type="text"
                placeholder="Search by project name or description"
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Client Filter */}
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Client Filter</label>
            <div className="relative">
              <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={clientFilter}
                onChange={handleClientFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.client_short_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full sm:w-48">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status Filter</label>
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white"
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                Actions
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px]">
                <div className="space-y-2">
                  <div>Client Name</div>
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("client_project_name")}
              >
                <div className="flex items-center gap-1">
                  Project Name
                  {getSortIcon("client_project_name")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[250px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("client_project_description")}
              >
                <div className="flex items-center gap-1">
                  Description
                  {getSortIcon("client_project_description")}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("is_active")}
              >
                <div className="flex items-center gap-1">
                  Status
                  {getSortIcon("is_active")}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                <div className="space-y-2">
                  <div>Project Count</div>
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon("created_at")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={14} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <span className="text-sm text-gray-600">Loading client projects...</span>
                  </div>
                </td>
              </tr>
            ) : clientProjects.length === 0 ? (
              <tr>
                <td colSpan={14} className="px-4 sm:px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaBuilding className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No client projects found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting your search or create a new client project
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              clientProjects.map((clientProject) => (
                <tr key={clientProject.id} className="hover:bg-gray-50 transition-colors">
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission("ClientProject", "edit") && (
                        <button
                          onClick={() => onEditClientProject(clientProject)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit client project"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {
                        // hasPermission('ClientProject', 'delete') && (
                        // <button
                        //   onClick={() => onDeleteClientProject(clientProject)}
                        //   className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                        //   title="Delete client project"
                        // >
                        //   <FaTrash className="w-4 h-4" />
                        // </button>
                        // )
                      }
                    </div>
                  </td>

                  {/* Client Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {clientProject.client?.client_full_name || "N/A"}
                      </p>
                      <p className="text-xs text-gray-500">{clientProject.client?.client_short_name || "N/A"}</p>
                    </div>
                  </td>

                  {/* Project Name */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {clientProject.client_project_name}
                      </p>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {clientProject.client_project_description ? (
                        <span className="line-clamp-2">{clientProject.client_project_description}</span>
                      ) : (
                        <span className="text-gray-400 italic">No description provided</span>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(clientProject.is_active)}</td>

                  {/* Project Count */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {clientProject.project_count || 0}
                    </span>
                  </td>

                  {/* Created At */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <span>{formatDate(clientProject.created_at)}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="client_projects"
        title="Export Client Projects Data"
        description="Download the filtered client projects with detailed context"
      />
    </div>
  )
}
